import { Badge, Button, Card, Col, Container, Image, Row, Form, Modal } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import Loader from '../../components/layout/Loader';
import Message from '../../components/layout/Message';
import Meta from '../../components/layout/Meta';
import {
    useDeliverOrderMutation,
    useGetOrderDetailsQuery,
    useRepeatOrderMutation,
    useGetInvoiceStatusQuery,
    useRateProductInOrderMutation,
    useValidateOrderStockMutation,
    useUpdateOrderItemMutation,
    useRemoveOrderItemMutation,
} from '../../slices/orderApiSlice';
import TotalPrice from '../../components/product/TotalPrice';
import ProductPrice from '../../components/product/ProductPrice';
import { FaWhatsapp, FaTruck, FaUser, FaBoxOpen, FaCheckCircle, FaExclamationCircle, FaFileInvoice, FaSyncAlt, FaCreditCard, FaCalendarAlt } from 'react-icons/fa';
import ShippingAddress from '../../components/checkout/ShippingAddress';
import BillingAddress from '../../components/checkout/BillingAddress';
import PayPhoneBox from '../../components/checkout/PayPhoneBox';
import EstimatedDelivery from '../../components/checkout/EstimatedDelivery';
import InvoiceLoadingState from '../../components/checkout/InvoiceLoadingState';
import QuotationControls from '../../components/checkout/QuotationControls';
import DownloadInvoiceButtons from '../../components/checkout/DownloadInvoiceButtons';
import InvoiceErrorState from '../../components/checkout/InvoiceErrorState';
import ContificoCodeBadge from '../../components/ui/ContificoCodeBadge';
import { FreeShippingBadge, ProductRowBenefitNotes } from '../../components/product/ProductBenefitMarks';
import OrderPriceSummary from '../../components/checkout/OrderPriceSummary';
import { selectActiveGlobalDiscount } from '../../slices/activeGlobalDiscountSlice';
import { readAppliedCoupon, resolveSummaryDiscount } from '../../utils/activeGlobalDiscount';
import { getEstimatedDeliveryDate } from '../../utils/deliveryDateUtils';
import useExitGuard from '../../utils/useExitGuard';
import useIsMobile from '../../utils/useIsMobile';
import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import '../../assets/styles/order.screen.css';
import QuantitySelector from '../../components/ui/QuantitySelector';

const OrderScreen = () => {
    const isMobile = useIsMobile();

    const { userInfo } = useSelector((state) => state.auth);
    const { companiesByRuc } = useSelector((state) => state.companies);
    const activeGlobalDiscount = useSelector(selectActiveGlobalDiscount);
    const { id: orderId } = useParams();
    const [searchParams] = useSearchParams();
    const showPaymentError = searchParams.get('error') === 'payment';
    const showStockError = searchParams.get('error') === 'stock';

    const navigate = useNavigate();

    const { data: order, refetch, isLoading, error } = useGetOrderDetailsQuery(orderId);

    const isAdmin = userInfo?.isAdmin;
    const isDistributor = userInfo?.priceAssigned === 'pvp_distribuidor';

    const [deliverOrder, { isLoading: loadingDeliver }] = useDeliverOrderMutation();
    const [repeatOrder, { isLoading: loadingRepeatOrder }] = useRepeatOrderMutation();
    const [rateProduct, { isLoading: isRating, error: ratingError }] = useRateProductInOrderMutation();
    const [validateOrderStock, { isLoading: isValidatingRetryStock }] = useValidateOrderStockMutation();
    const [updateOrderItem] = useUpdateOrderItemMutation();
    const [removeOrderItem] = useRemoveOrderItemMutation();

    const [pollingInterval, setPollingInterval] = useState(0);
    const [showRatingModal, setShowRatingModal] = useState(false);
    const [rating, setRating] = useState(0);
    const [hover, setHover] = useState(null);
    const [comment, setComment] = useState('');
    const [selectedProduct, setSelectedProduct] = useState(null);
    const paymentRetryRef = useRef(null);
    const [showRetryScrollButton, setShowRetryScrollButton] = useState(false);
    const [retryStockErrors, setRetryStockErrors] = useState([]);
    const [retryStockValidationMessage, setRetryStockValidationMessage] = useState('');
    const [isRetryStockValidated, setIsRetryStockValidated] = useState(false);

    const pendingContificoStatuses = ['PENDING_SRI', 'RETRY_PENDING', 'PENDING'];
    const contifico = Array.isArray(order?.contifico) ? order.contifico[0] : order?.contifico;
    const contificoStatus = contifico?.status;
    const rideUrl = contifico?.urlRide || contifico?.rideUrl || contifico?.raw?.url_ride;
    const xmlUrl = contifico?.urlXml || contifico?.xmlUrl || contifico?.raw?.url_xml;
    const hasInvoiceLinks = Boolean(rideUrl || xmlUrl);
    const hasSriAuthorizationData = Boolean(contifico?.raw?.autorizacion || contifico?.authorization);

    const isInvoiceReady =
        contificoStatus === 'AUTHORIZED' ||
        order?.orderStatus === 'INVOICED' ||
        (hasInvoiceLinks && hasSriAuthorizationData);

    const isInvoiceFailed =
        order?.orderStatus === 'INVOICE_FAILED' ||
        order?.orderStatus === 'RETRY_PENDING' ||
        contificoStatus === 'FAILED' ||
        contificoStatus === 'RETRY_PENDING';

    // Check if order has pending invoice status and no explicit ready/failed state yet
    const isInvoicePending =
        !isInvoiceReady &&
        !isInvoiceFailed &&
        (
            order?.orderStatus === 'INVOICE_PENDING' ||
            order?.orderStatus === 'PAYMENT_CONFIRMED' ||
            pendingContificoStatuses.includes(contificoStatus)
        );

    const invoiceBadgeState = (() => {
        if (order?.orderStatus === 'QUOTATION_PENDING') {
            return { variant: 'warning', label: 'Cotización Pendiente' };
        }

        if (isInvoiceFailed) {
            return { variant: 'danger', label: 'Error en Factura ⚠️' };
        }

        if (isInvoiceReady) {
            return { variant: 'success', label: 'Facturado ✓' };
        }

        if (isInvoicePending) {
            return { variant: 'info', label: 'Procesando Factura ⏳' };
        }

        return { variant: 'warning', label: 'Factura Pendiente' };
    })();

    const showInvoiceProcessingBox =
        !!order?.isPaid &&
        order?.orderStatus !== 'QUOTATION_PENDING' &&
        isInvoicePending;

    const { data: invoiceStatusData } = useGetInvoiceStatusQuery(orderId, {
        pollingInterval,
        skip: !orderId || !isInvoicePending || isInvoiceReady,
    });

    useEffect(() => {
        // Poll for pending invoice states - check every 60 seconds
        // This includes PAYMENT_CONFIRMED, INVOICE_PENDING, and contifico statuses like PENDING_SRI, RETRY_PENDING
        if (isInvoicePending) {
            setPollingInterval(60000); // Poll every 60 seconds as per requirements
        } else {
            setPollingInterval(0);
        }
    }, [isInvoicePending]);

    useEffect(() => {
        if (invoiceStatusData?.status === 'AUTHORIZED' || invoiceStatusData?.status === 'FAILED') {
            setPollingInterval(0);
            refetch();
        }
    }, [invoiceStatusData, refetch]);

    // Exponential backoff for polling
    useEffect(() => {
        let timer;
        if (pollingInterval > 0 && pollingInterval < 60000) {
            timer = setTimeout(() => {
                setPollingInterval((prev) => Math.min(prev * 1.5, 60000));
            }, pollingInterval * 2);
        }
        return () => clearTimeout(timer);
    }, [pollingInterval]);

    const repeatOrderHandler = async () => {
        try {
            await repeatOrder(orderId).unwrap();
            navigate('/cart');
        } catch (err) {
            toast.error(err?.data?.message || err.error);
        }
    };

    const deliverOrderHandler = async () => {
        try {
            await deliverOrder({ orderId, companyId: order.company?._id });
            refetch();
            toast.success('Pedido Enviado');
        } catch (err) {
            toast.error(err?.data?.message || err.error);
        }
    };

    const unpaidWhatsAppMessage = 'Hola, me gustaría solicitar el link de pago de mi pedido: ';
    const paidWhatsAppMessage = 'Hola, me gustaría consultar el estado de mi pedido: ';

    const handleWhatsAppClick = () => {
        const isFullyPaid = order.isPaid;
        const message = encodeURIComponent(
            `${isFullyPaid ? paidWhatsAppMessage : unpaidWhatsAppMessage}\nhttps://homesanty.com/login?redirect=/order/${order._id}`
        );
        const whatsappUrl = `https://api.whatsapp.com/send?phone=${process.env.REACT_APP_WHATSAPP_PHONE}&text=${message}`;
        window.open(whatsappUrl, '_blank');
    };

    const deliveryTag = order?.shippingAddress?.label;
    const canEstimateDelivery = !!deliveryTag && deliveryTag !== 'pickup';
    const deliveryBaseDateSource = order?.isPaid ? order?.paidAt : order?.createdAt;
    const deliveryBaseDate = deliveryBaseDateSource ? new Date(deliveryBaseDateSource) : new Date();
    const estimatedDeliveryRange = canEstimateDelivery
        ? getEstimatedDeliveryDate(deliveryTag, deliveryBaseDate)
        : null;
    const isWithinEstimatedWindow = estimatedDeliveryRange
        ? new Date() <= estimatedDeliveryRange.max
        : false;
    const showEstimatedDeliverySection =
        canEstimateDelivery && (!order?.isDelivered || isWithinEstimatedWindow);
    const showDeliveryIssueWhatsApp =
        !!order?.isDelivered && isWithinEstimatedWindow && userInfo?._id === order?.user?._id;

    const handleDeliveryIssueWhatsAppClick = () => {
        const message = encodeURIComponent(
            `Hola, tengo problemas con este pedido (${order._id}). ¿Me pueden ayudar, por favor?\nhttps://homesanty.com/login?redirect=/order/${order._id}`
        );
        const whatsappUrl = `https://api.whatsapp.com/send?phone=${process.env.REACT_APP_WHATSAPP_PHONE}&text=${message}`;
        window.open(whatsappUrl, '_blank');
    };

    const orderDiscountAmount = Number(order?.company?.discountAmount ?? order?.discountAmount ?? 0);
    const orderItemsPrice = Number(order?.company?.itemsPrice ?? order?.itemsPrice ?? 0);
    const orderPreDiscountItemsPrice = Number(
        order?.company?.preDiscountItemsPrice ??
        order?.preDiscountItemsPrice ??
        (orderDiscountAmount > 0 ? orderItemsPrice + orderDiscountAmount : orderItemsPrice)
    );
    const orderShippingPrice = Number(order?.shippingPrice ?? 0);
    const orderTaxPrice = Number(order?.taxPrice ?? 0);
    const orderCompanyRuc =
        (typeof order?.company === 'string' ? order.company : null) ||
        order?.company?.ruc ||
        order?.company?.companyRuc ||
        order?.companyRuc ||
        null;
    const summaryDiscount = resolveSummaryDiscount({
        activeGlobalDiscount,
        companyRuc: orderCompanyRuc,
        appliedCoupon: readAppliedCoupon(order?.company) || readAppliedCoupon(order),
    });
    const pickupCompanyInfo = useMemo(() => {
        if (order?.shippingAddress?.label !== 'pickup') return null;
        const directoryCompany = (orderCompanyRuc && companiesByRuc?.[orderCompanyRuc]) || {};
        const orderCompany = order?.company && typeof order.company === 'object' ? order.company : {};
        const directoryPoints = Array.isArray(directoryCompany.additionalPickupLocations)
            ? directoryCompany.additionalPickupLocations
            : [];
        const orderPoints = Array.isArray(orderCompany.additionalPickupLocations)
            ? orderCompany.additionalPickupLocations
            : [];

        return {
            ...directoryCompany,
            ...orderCompany,
            location: orderCompany.location || directoryCompany.location,
            openingHours: Array.isArray(orderCompany.openingHours) && orderCompany.openingHours.length
                ? orderCompany.openingHours
                : directoryCompany.openingHours,
            additionalPickupLocations: orderPoints.length ? orderPoints : directoryPoints,
        };
    }, [order, orderCompanyRuc, companiesByRuc]);
    const [retryClientTransactionId] = useState(() =>
        orderId ? `${orderId}-${Date.now()}` : `retry-${Date.now()}`
    );

    const canRetryPayPhonePayment =
        !order?.isPaid &&
        (userInfo?._id === order?.user?._id || isAdmin) &&
        order?.paymentMethod === 'PayPhone' &&
        Boolean(orderCompanyRuc);

    const hasRetryStockIssues = retryStockErrors.length > 0;

    const validateRetryStock = useCallback(async () => {
        if (!orderId || !canRetryPayPhonePayment) return;

        try {
            const validationRes = await validateOrderStock(orderId).unwrap();
            if (validationRes.ok || validationRes.success) {
                setRetryStockErrors([]);
                setRetryStockValidationMessage('');
            } else {
                setRetryStockErrors(validationRes.unavailableItems || []);
                setRetryStockValidationMessage(
                    validationRes.message || 'Algunos productos ya no tienen stock disponible.'
                );
            }
            setIsRetryStockValidated(true);
        } catch (err) {
            setRetryStockErrors(err?.data?.unavailableItems || []);
            setRetryStockValidationMessage(
                err?.data?.message || err?.error || 'No se pudo validar el stock del pedido.'
            );
            setIsRetryStockValidated(true);
        }
    }, [orderId, canRetryPayPhonePayment, validateOrderStock]);

    const getRetryStockErrorForItem = (item) => {
        return retryStockErrors.find((err) =>
            err?.itemId === item?._id ||
            err?._id === item?._id ||
            err?.productId === item?.product
        );
    };

    const handleRetryQtyChange = async (item, qty) => {
        if (!item?._id) return;
        try {
            await updateOrderItem({ orderId, itemId: item._id, qty }).unwrap();
            setIsRetryStockValidated(false);
            setRetryStockErrors([]);
            setRetryStockValidationMessage('');
            await validateRetryStock();
            refetch();
        } catch (err) {
            toast.error(err?.data?.message || err?.error || 'No se pudo actualizar la cantidad.');
        }
    };

    const handleRemoveUnavailableRetryItem = async (item) => {
        const itemError = getRetryStockErrorForItem(item);
        const itemId = item?._id || itemError?.itemId;
        const requested = Number(item?.requested || 0);
        const available = Number(itemError?.available ?? item?.available ?? 0);

        if (!itemId) {
            toast.error('No se pudo identificar el producto para actualizar el pedido.');
            return;
        }

        try {
            if (available <= 0) {
                await removeOrderItem({ orderId, itemId }).unwrap();
                toast.success('Producto eliminado del pedido');
            } else if (available < requested) {
                await updateOrderItem({ orderId, itemId, qty: available }).unwrap();
                toast.success(`Cantidad actualizada a ${available}`);
            } else {
                await removeOrderItem({ orderId, itemId }).unwrap();
                toast.success('Producto eliminado del pedido');
            }
            setIsRetryStockValidated(false);
            setRetryStockErrors([]);
            setRetryStockValidationMessage('');
            await validateRetryStock();
            refetch();
        } catch (err) {
            toast.error(err?.data?.message || err?.error || 'No se pudo actualizar el pedido.');
        }
    };

    // Block page exit only when PayPhoneBox is actually mounted (same reason PlaceOrderScreen uses this)
    useExitGuard(canRetryPayPhonePayment);

    const canShowRetryPaymentMissingCompanyMessage =
        !order?.isPaid &&
        (userInfo?._id === order?.user?._id || isAdmin) &&
        order?.paymentMethod === 'PayPhone' &&
        !orderCompanyRuc;
    const { retryAmountWithTax, retryAmountWithoutTax } = useMemo(() => {
        const totalBase = orderItemsPrice + orderShippingPrice;

        if (orderTaxPrice === 0) {
            return {
                retryAmountWithTax: 0,
                retryAmountWithoutTax: totalBase,
            };
        }

        return {
            retryAmountWithTax: totalBase,
            retryAmountWithoutTax: 0,
        };
    }, [orderItemsPrice, orderShippingPrice, orderTaxPrice]);

    useEffect(() => {
        if (!canRetryPayPhonePayment || !retryClientTransactionId || typeof window === 'undefined') {
            return;
        }

        window.sessionStorage.setItem(
            `payphoneContext:${retryClientTransactionId}`,
            JSON.stringify({
                flow: 'orderRetry',
                orderId,
                companyRuc: orderCompanyRuc,
            })
        );
    }, [canRetryPayPhonePayment, retryClientTransactionId, orderId, orderCompanyRuc]);

    useEffect(() => {
        if (!isMobile || !canRetryPayPhonePayment) {
            setShowRetryScrollButton(false);
            return;
        }

        const target = paymentRetryRef.current;
        if (!target) {
            return;
        }

        const observer = new IntersectionObserver(
            ([entry]) => {
                setShowRetryScrollButton(!entry.isIntersecting);
            },
            { threshold: 0.2 }
        );

        observer.observe(target);

        return () => {
            observer.unobserve(target);
        };
    }, [isMobile, canRetryPayPhonePayment]);

    useEffect(() => {
        if (!canRetryPayPhonePayment || isRetryStockValidated) return;
        validateRetryStock();
    }, [canRetryPayPhonePayment, isRetryStockValidated, validateRetryStock]);

    const handleOpenRatingModal = (product) => {
        setSelectedProduct(product);
        setShowRatingModal(true);
        setRating(0);
        setHover(null);
        setComment('');
    };
    const handleCloseRatingModal = () => {
        setShowRatingModal(false);
        setSelectedProduct(null);
        setRating(0);
        setHover(null);
        setComment('');
    };
    const handleSubmitRating = async (e) => {
        e.preventDefault();
        if (!rating) {
            toast.error('Por favor selecciona una calificación.');
            return;
        }
        try {
            await rateProduct({ orderId, productId: selectedProduct.product, rating, comment }).unwrap();
            toast.success('¡Gracias por tu calificación!');
            handleCloseRatingModal();
            refetch();
        } catch (err) {
            toast.error(err?.data?.message || 'Error al enviar la calificación.');
        }
    };

    const getItemVariationEntries = (item) => {
        if (Array.isArray(item?.selectedVariations) && item.selectedVariations.length > 0) {
            return item.selectedVariations
                .filter((variation) => variation?.variationType && variation?.option)
                .map((variation) => ({
                    variationType: variation.variationType,
                    option: variation.option,
                }));
        }

        if (item?.selectedVariations && typeof item.selectedVariations === 'object') {
            return Object.entries(item.selectedVariations)
                .map(([variationType, selectedOption]) => {
                    if (!selectedOption) return null;

                    if (typeof selectedOption === 'string') {
                        return { variationType, option: selectedOption };
                    }

                    const option = selectedOption.option || selectedOption.name;
                    if (!option) return null;

                    return { variationType, option };
                })
                .filter(Boolean);
        }

        if (item?.variation?.variationType && item?.variation?.option) {
            return [
                {
                    variationType: item.variation.variationType,
                    option: item.variation.option,
                },
            ];
        }

        return [];
    };

    return isLoading ? (
        <Loader />
    ) : error ? (
        <Message variant="danger">
            {error?.data?.message || error?.error || 'Algo salió mal'}
        </Message>
    ) : order ? (
        <Container className="order-screen-container py-4">
            <Meta title={`Pedido ${order._id}`} />
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4">
                <div>
                    <h1 className="h2 mb-2">Detalles del Pedido</h1>
                    <div className="order-id-badge">ID: {order._id}</div>
                </div>
            </div>

            {showPaymentError && (
                <Message variant="danger">
                    No pudimos verificar el pago. Por favor contacta con soporte.
                </Message>
            )}
            {showStockError && (
                <Message variant="warning">
                    No pudimos reintentar el pago porque el stock cambió. Revisa tu pedido o contacta soporte.
                </Message>
            )}

            <Row>
                <Col lg={8}>
                    <Card className="order-section-card p-4">
                        <h2 className="order-section-title">
                            <FaUser className="text-primary" /> Información del Cliente
                        </h2>
                        <div className="info-grid">
                            <div className="mb-3">
                                <strong>Nombre:</strong>
                                <p className="mb-0 text-muted">{order.user.name}</p>
                            </div>
                            <div className="mb-3">
                                <strong>Email:</strong>
                                <p className="mb-0 text-muted">
                                    <a href={`mailto:${order.user.email}`} className="text-decoration-none text-muted">
                                        {order.user.email}
                                    </a>
                                </p>
                            </div>
                        </div>

                        <hr className="my-4" />

                        <div className="mb-4">
                            <ShippingAddress
                                address={order.shippingAddress}
                                showEstimatedDelivery={false}
                                pickupCompanyInfo={pickupCompanyInfo || order.company}
                            />
                        </div>

                        <hr className="my-4" />

                        <div>
                            <BillingAddress
                                billingAddress={order.billingAddress}
                                showTittle={true}
                            />
                        </div>
                    </Card>

                    <Card className="order-section-card p-4 mb-4 mt-4 shadow-sm border-0">
                        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2 border-bottom pb-3">
                            <div className="d-flex align-items-center gap-3">
                                {order.company?.logoUrl && (
                                    <Image
                                        src={order.company.logoUrl}
                                        alt={order.company.name}
                                        className="company-logo-cart"
                                        style={{ width: '40px', height: '40px', objectFit: 'contain' }}
                                    />
                                )}
                                <div className="d-flex flex-column">
                                    <h2 className="order-section-title mb-0 h5 fw-bold border-0 pb-0">
                                        <FaBoxOpen className="text-primary me-2" />
                                        {order.company?.name || 'Comercial Santy'}
                                    </h2>
                                    <div className="d-flex gap-2 mt-1">
                                        <Button 
                                            variant="link" 
                                            size="sm" 
                                            className="p-0 text-success text-decoration-none d-flex align-items-center gap-1"
                                            style={{ fontSize: '0.75rem' }}
                                            onClick={() => {
                                                const msg = encodeURIComponent(`Hola, tengo una duda sobre mi pedido ${order._id} de la empresa ${order.company?.name}`);
                                                window.open(`https://api.whatsapp.com/send?phone=${process.env.REACT_APP_WHATSAPP_PHONE}&text=${msg}`, '_blank');
                                            }}
                                        >
                                            <FaWhatsapp /> Ayuda con este vendedor
                                        </Button>
                                    </div>
                                </div>
                            </div>
                            <div className="d-flex gap-2 flex-wrap">
                                <div className={`status-badge ${order.isPaid ? 'success' : 'danger'}`}>
                                    {order.isPaid ? <FaCheckCircle /> : <FaExclamationCircle />}
                                    <span className="ms-1">{order.isPaid ? 'Pagado' : 'Pago Pendiente'}</span>
                                </div>
                                <div className={`status-badge ${order.isDelivered ? 'success' : 'warning'}`}>
                                    <FaTruck />
                                    <span className="ms-1">{order.isDelivered ? 'Enviado' : 'No Enviado'}</span>
                                </div>
                                <div className={`status-badge ${invoiceBadgeState.variant}`}>
                                    <FaFileInvoice />
                                    <span className="ms-1">{invoiceBadgeState.label}</span>
                                </div>
                            </div>
                        </div>

                        <div className="order-items-list mb-3">
                            {order.orderItems.map((item, idx) => (
                                (() => {
                                    const retryStockError = getRetryStockErrorForItem(item);
                                    const isOutOfStock = Boolean(retryStockError);
                                    return (
                                <div key={idx} className="order-item-row py-3 border-bottom">
                                    <Row className="align-items-center">
                                        <Col xs={3} md={2}>
                                            <Image
                                                src={item.image || '/images/default.jpg'}
                                                alt={item.name}
                                                fluid
                                                rounded
                                                style={{ maxWidth: '60px' }}
                                                onError={(e) => {
                                                    e.target.onerror = null;
                                                    e.target.src = '/images/default.jpg';
                                                }}
                                            />
                                        </Col>
                                        <Col xs={9} md={6}>
                            <Link
                                to={`/product/${item.product}`}
                                className="text-decoration-none fw-bold text-dark"
                            >
                                {item.name}
                            </Link>
                                            <FreeShippingBadge product={item} companyRuc={orderCompanyRuc} />
                                            <ProductRowBenefitNotes product={item} companyRuc={orderCompanyRuc} />
                                            <ContificoCodeBadge
                                                code={item?.contificoCode}
                                                compact
                                                className="mt-1"
                                            />
                                            {getItemVariationEntries(item).map((variation, i) => (
                                                <div
                                                    key={`${variation.variationType}-${variation.option}-${i}`}
                                                    className="text-muted very-small mt-1"
                                                >
                                                    <span className="fw-semibold">{variation.variationType}:</span> {variation.option}
                                                </div>
                                            ))}
                                            <div className="text-muted very-small mt-1">
                                                Cantidad: {item.qty}
                                            </div>
                                            {isOutOfStock && (
                                                <div className="text-danger small fw-bold mt-1">
                                                    {retryStockError.message || 'Producto agotado o insuficiente'}
                                                </div>
                                            )}
                                        </Col>
                                        <Col xs={12} md={4} className="text-end mt-2 mt-md-0">
                                            <ProductPrice
                                                product={item}
                                                userInfo={userInfo}
                                                qty={item.qty}
                                                size="lg"
                                                className="justify-content-end"
                                            />
                                            {(() => {
                                                const productDiscount = Number(item?.discount || 0);
                                                const couponItemDiscount = Number(item?.couponDiscountAmount || item?.couponDiscount || 0);
                                                return couponItemDiscount > 0 && productDiscount > 0 && productDiscount > couponItemDiscount;
                                            })() && (
                                                <Badge bg="info" className="mt-1">Mayor descuento aplicado</Badge>
                                            )}
                                            {userInfo?._id === order.user?._id && (
                                                <Button
                                                    size="sm"
                                                    className="mt-2 fw-bold rate-product-btn"
                                                    onClick={() => handleOpenRatingModal(item)}
                                                >
                                                    Calificar producto
                                                </Button>
                                            )}
                                            {isOutOfStock && (
                                                <div className="mt-2 d-flex justify-content-end gap-2">
                                                    <div style={{ width: '110px' }}>
                                                        <QuantitySelector
                                                            value={item.qty}
                                                            max={item.countInStock || 999}
                                                            onChange={(val) => handleRetryQtyChange(item, val)}
                                                            size="sm"
                                                            debounceMs={500}
                                                            allowDecrease
                                                        />
                                                    </div>
                                                    <Button
                                                        type="button"
                                                        size="sm"
                                                        variant="outline-danger"
                                                        onClick={() => handleRemoveUnavailableRetryItem(item)}
                                                    >
                                                        Quitar
                                                    </Button>
                                                </div>
                                            )}
                                        </Col>
                                    </Row>
                                </div>
                                    );
                                })()
                            ))}
                        </div>

                        <div className="bg-light p-3 rounded mb-3">
                            <OrderPriceSummary
                                subtotal={orderPreDiscountItemsPrice}
                                discountAmount={orderDiscountAmount}
                                discount={summaryDiscount}
                                shippingPrice={orderShippingPrice}
                                taxPrice={orderTaxPrice}
                                totalLabel={order.company?.name ? `Total ${order.company.name}:` : 'Total:'}
                                total={(
                                    <TotalPrice
                                        price={order.totalPrice}
                                        size="md"
                                        className="justify-content-end"
                                    />
                                )}
                            />
                        </div>

                        {/* Delivery Estimation Section */}
                        {showEstimatedDeliverySection && (
                            <div className="mb-3 p-3 border rounded bg-white small">
                                <h6 className="fw-bold mb-2 d-flex align-items-center gap-2">
                                    <FaCalendarAlt className="text-success" /> Estimación de Entrega
                                </h6>
                                <EstimatedDelivery 
                                    tag={deliveryTag}
                                    orderDate={deliveryBaseDate}
                                    isPaid={order.isPaid}
                                />
                                {showDeliveryIssueWhatsApp && (
                                    <Button
                                        variant="outline-success"
                                        size="sm"
                                        className="mt-2 d-inline-flex align-items-center gap-2"
                                        onClick={handleDeliveryIssueWhatsAppClick}
                                    >
                                        <FaWhatsapp /> Tengo problemas con este pedido
                                    </Button>
                                )}
                            </div>
                        )}

                        {/* Invoice Management Workflow */}
                        {/* Show loading/pending state for any pending invoice status */}
                        {showInvoiceProcessingBox && (
                            <InvoiceLoadingState
                                orderId={orderId}
                                contificoStatus={contificoStatus}
                                onComplete={() => refetch()}
                            />
                        )}

                        {/* Distributor: Quotation pending invoice creation */}
                        {isDistributor && order.orderStatus === 'QUOTATION_PENDING' && (
                            <QuotationControls
                                orderId={orderId}
                                isAdmin={isAdmin}
                                onSuccess={() => refetch()}
                            />
                        )}

                        {/* Invoice authorized - show download buttons ONLY when truly authorized */}
                        {isInvoiceReady && (
                            <DownloadInvoiceButtons order={order} />
                        )}

                        {/* Invoice creation/authorization failed */}
                        {isInvoiceFailed && (
                            <InvoiceErrorState
                                orderId={orderId}
                                errorMessage={contifico?.lastError}
                                onSuccess={() => refetch()}
                            />
                        )}

                        {/* Admin Delivery Action */}
                        {isAdmin && order.isPaid && !order.isDelivered && (
                            <div className="admin-actions p-3 border rounded mb-3 bg-light shadow-sm">
                                <h6 className="fw-bold mb-3 small d-flex align-items-center gap-2">
                                    <FaTruck className="text-primary" /> Gestión de Entrega
                                </h6>
                                <Button
                                    type="button"
                                    variant="primary"
                                    size="sm"
                                    className="w-100 py-2 fw-bold shadow-sm"
                                    onClick={deliverOrderHandler}
                                    disabled={loadingDeliver}
                                >
                                    {loadingDeliver ? '...' : 'Marcar Como Enviado'}
                                </Button>
                            </div>
                        )}

                        {/* User PayPhone Action */}
                        {canRetryPayPhonePayment && (
                            <div id="retry-payment-section" ref={paymentRetryRef} className="mt-3 p-3 border rounded bg-white shadow-sm">
                                <h6 className="fw-bold mb-3 d-flex align-items-center gap-2 small">
                                    <FaCreditCard className="text-primary" /> Reintentar pago de este pedido
                                </h6>
                                <p className="text-muted small mb-3">
                                    Valida stock en tiempo real y luego te redirige al proceso seguro de confirmación.
                                </p>
                                {isValidatingRetryStock && (
                                    <Message variant="info">Validando stock del pedido...</Message>
                                )}
                                {hasRetryStockIssues && (
                                    <div className="mb-3">
                                        <Message variant="danger">
                                            {retryStockValidationMessage || 'Revisa los productos sin stock para continuar.'}
                                        </Message>
                                    </div>
                                )}
                                {!hasRetryStockIssues && isRetryStockValidated && (
                                    <PayPhoneBox
                                        amount={order.totalPrice}
                                        amountWithTax={retryAmountWithTax}
                                        amountWithoutTax={retryAmountWithoutTax}
                                        tax={orderTaxPrice}
                                        clientTransactionId={retryClientTransactionId}
                                        companyId={orderCompanyRuc}
                                    />
                                )}
                            </div>
                        )}
                        {canShowRetryPaymentMissingCompanyMessage && (
                            <Message variant="warning">
                                No se puede reintentar el pago en este momento porque falta la empresa del pedido.
                                Por favor contacta a soporte.
                            </Message>
                        )}
                    </Card>

                </Col>

                <Col lg={4}>
                    <Card className="order-summary-card p-4 shadow-sm">
                        <h2 className="order-summary-title">Resumen de Orden</h2>
                        
                        <OrderPriceSummary
                            subtotal={orderPreDiscountItemsPrice}
                            discountAmount={orderDiscountAmount}
                            discount={summaryDiscount}
                            shippingPrice={orderShippingPrice}
                            taxPrice={orderTaxPrice}
                            total={(
                                <TotalPrice
                                    price={order.totalPrice}
                                    size="xl"
                                    className="justify-content-end"
                                />
                            )}
                        />

                        <div className="mt-4 d-grid gap-3">
                            <Button
                                variant="info"
                                className="w-100 d-flex justify-content-center align-items-center gap-2 py-2 fw-bold text-white shadow-sm"
                                style={{ backgroundColor: '#25D366', border: 'none' }}
                                onClick={handleWhatsAppClick}
                            >
                                <FaWhatsapp size={20} /> Solicitar Ayuda
                            </Button>

                            {userInfo?._id === order.user?._id && (
                                <div className="mt-2">
                                    {(order.orderStatus === 'CREATED' || order.orderStatus === 'CANCELLED') && (
                                        <Button
                                            variant="outline-secondary"
                                            className="w-100 py-2 fw-bold d-flex justify-content-center align-items-center gap-2"
                                            onClick={repeatOrderHandler}
                                            disabled={loadingRepeatOrder}
                                        >
                                            <FaSyncAlt /> Editar pedido
                                        </Button>
                                    )}
                                    {order.orderStatus === 'INVOICED' && (
                                        <Button
                                            variant="outline-secondary"
                                            className="w-100 py-2 fw-bold d-flex justify-content-center align-items-center gap-2"
                                            onClick={repeatOrderHandler}
                                            disabled={loadingRepeatOrder}
                                        >
                                            <FaSyncAlt /> Repetir pedido
                                        </Button>
                                    )}
                                </div>
                            )}

                            {loadingDeliver && <Loader />}
                        </div>
                    </Card>
                </Col>
            </Row>

            {isMobile && canRetryPayPhonePayment && showRetryScrollButton && (
                <Link
                    to="#retry-payment-section"
                    onClick={(e) => {
                        e.preventDefault();
                        const el = document.getElementById('retry-payment-section');
                        if (el) {
                            el.scrollIntoView({ behavior: 'smooth' });
                            setShowRetryScrollButton(false);
                        }
                    }}
                    className="floating-payment-btn"
                    style={{
                        position: 'fixed',
                        right: '1.5rem',
                        bottom: '6rem',
                        zIndex: 1050,
                        padding: '0.75rem 1.5rem',
                        backgroundColor: '#b5430f',
                        color: '#fff',
                        borderRadius: '50px',
                        boxShadow: '0 4px 15px rgba(181, 67, 15, 0.4)',
                        textDecoration: 'none',
                        fontWeight: '600',
                        fontSize: '1rem',
                    }}
                >
                    Ir a reintentar pago
                </Link>
            )}

            {/* Rating Modal */}
            <Modal show={showRatingModal} onHide={handleCloseRatingModal} centered>
                <Modal.Header closeButton style={{ background: '#fff1ea', borderBottom: '1px solid #efc8ba' }}>
                    <Modal.Title style={{ color: '#b5430f' }}>Calificar producto</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSubmitRating}>
                    <Modal.Body>
                        <div className="text-center mb-3">
                            <div className="review-rating-picker justify-content-center" role="radiogroup" aria-label="Calificación">
                                {[1,2,3,4,5].map((star) => (
                                    <button
                                        key={star}
                                        type="button"
                                        className={`review-star-btn ${star <= (hover || rating) ? 'is-active' : ''}`}
                                        onMouseEnter={() => setHover(star)}
                                        onMouseLeave={() => setHover(null)}
                                        onClick={() => setRating(star)}
                                        aria-label={`${star} estrella${star > 1 ? 's' : ''}`}
                                        aria-checked={rating === star}
                                        role="radio"
                                        style={{ fontSize: '2.2rem' }}
                                    >
                                        ★
                                    </button>
                                ))}
                            </div>
                            <div className="mt-2">
                                {rating === 1 && 'Poco Aceptable'}
                                {rating === 2 && 'Aceptable'}
                                {rating === 3 && 'Bueno'}
                                {rating === 4 && 'Muy Bueno'}
                                {rating === 5 && 'Excelente!'}
                            </div>
                        </div>
                        <Form.Group controlId="comment">
                            <Form.Label column='lg'>Comentario (opcional)</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                value={comment}
                                onChange={(e) => setComment(e.target.value)}
                                placeholder="Escribe un comentario..."
                            />
                        </Form.Group>
                        {ratingError && <Message variant="danger">{ratingError?.data?.message || 'Error al enviar la calificación.'}</Message>}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleCloseRatingModal} disabled={isRating}>
                            Cancelar
                        </Button>
                        <Button
                            type="submit"
                            className="rate-product-btn"
                            disabled={isRating}
                        >
                            {isRating ? 'Enviando...' : 'Enviar calificación'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </Container>
    ) : (
        <Loader />
    );
};

export default OrderScreen;
